import type { TripDates } from "./date-picker";
import { find, findAll } from "./dom";
import { FormTokenSession, csrfHeader } from "./form-token";
import { checkReviewChoices, initReviewForms, reviewPayload } from "./review-form";

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

/**
 * Telefonnummer. Med flit tillatande: siffror, mellanslag, plus, bindestreck
 * och parenteser, minst sju siffror. Ett strangare monster hade avvisat
 * riktiga nummer — utlandska, med riktnummer i parentes, med snedstreck — och
 * ett formular som sager nej till ett giltigt nummer ar varre an ett som
 * slapper igenom ett skevt.
 */
const PHONE = /^[+()\d][\s\-()/\d]{5,}$/;

interface Field {
  id: string;
  emptyMessage: string;
  /** Formatkontroll utover att faltet ar ifyllt. */
  kind?: "email" | "tel";
  /** Valfritt falt: tomt ar godkant, men ar det ifyllt kontrolleras formatet. */
  optional?: boolean;
  /** The fewest characters the server accepts. */
  minLength?: number;
  /** Refuses anything that reads as a web address, as the server does. */
  noLinks?: boolean;
}

/** Each form owns its token; contact operations must never be shared with the newsletter. */
const tokenSessions = new WeakMap<HTMLFormElement, FormTokenSession>();
const inFlight = new WeakSet<HTMLFormElement>();

/** Returns the per-form token owner without adding state to cached HTML. */
function tokenSession(form: HTMLFormElement): FormTokenSession {
  let session = tokenSessions.get(form);
  if (session === undefined) {
    session = new FormTokenSession();
    tokenSessions.set(form, session);
  }
  return session;
}

/** Fetches a purpose-specific token and keeps the existing minimum-age guard. */
async function prepareToken(form: HTMLFormElement, submitting = false): Promise<void> {
  const url = form.dataset["tokenUrl"];
  if (url === undefined) {
    return;
  }
  // Session-bound forms name their CSRF header and a lock of their own in the markup; the review
  // form still uses the older timestamp token and names neither.
  const sessionBound = csrfHeader(form.dataset) !== null;
  const lock = form.dataset["tokenLock"];
  const result = await tokenSession(form).get(async () => {
    const load = async (): Promise<{ token: string; csrfToken?: string }> => {
      const response = await fetch(url, { cache: "no-store", credentials: "same-origin" });
      if (!response.ok) {
        throw new Error("Form unavailable");
      }
      const value = await response.json() as { token: string; csrfToken?: string };
      if (typeof value.token !== "string" || value.token.length === 0
          || (sessionBound && typeof value.csrfToken !== "string")) {
        throw new Error("Missing form security token");
      }
      return value;
    };
    // Serialize initial cookie issuance across tabs where Web Locks is available.
    return lock !== undefined && navigator.locks !== undefined
      ? navigator.locks.request(lock, load)
      : load();
  });
  form.dataset["token"] = result.token;
  if (result.csrfToken !== undefined) {
    form.dataset["csrfToken"] = result.csrfToken;
  }
  if (submitting) {
    const remaining = 3100 - (Date.now() - result.received);
    if (remaining > 0) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, remaining));
    }
  }
}

const prepared = new WeakSet<HTMLFormElement>();

/**
 * Prepares a form once: enables its fields now and fetches its token when someone starts on it.
 *
 * <p>The token response is what sets a form's security cookie. Fetched on page load, every visitor
 * got that cookie whether or not they ever typed a word — on every page, since the newsletter sits
 * in the footer. Focus is early enough: the server wants the token 3.1 seconds old, which typing
 * an address takes anyway, and `send` fetches the token itself should the early fetch fail.</p>
 *
 * <p>A form marked `data-token-lazy` is prepared only when it is opened. The review form sits
 * folded on every accommodation page, and spending the shared token allowance on every visitor
 * who never unfolds it would be waste.</p>
 */
function prepareOnce(form: HTMLFormElement): Promise<void> {
  if (prepared.has(form)) {
    return Promise.resolve();
  }
  prepared.add(form);
  markReady(form);
  form.addEventListener("focusin", () => {
    // A failure here is not the visitor's to see yet; `send` fetches again and reports it then.
    void prepareToken(form).catch(() => undefined);
  }, { once: true });
  return Promise.resolve();
}

/**
 * Gemensam formulärhantering.
 *
 * Två regler genom hela filen:
 *   * Felet skrivs bredvid fältet det gäller, inte bara i en sammanfattning.
 *   * Svaret hamnar i ett `aria-live`-område, annars når det aldrig
 *     skärmläsare — vilket är precis felet i nuvarande sajt, där
 *     kontaktformulärets JSON-reply renderas in utan att annonseras.
 */
function setError(form: HTMLFormElement, name: string, message: string): void {
  const box = find<HTMLElement>(`[data-error-for="${name}"]`, form);
  if (box !== null) {
    box.textContent = message;
  }
}

function status(form: HTMLFormElement, message: string): void {
  const el = find<HTMLElement>("[data-status]", form);
  if (el !== null) {
    el.textContent = message;
  }
}

function lockButton(form: HTMLFormElement): void {
  const button = find<HTMLButtonElement>('button[type="submit"]', form);
  if (button !== null) {
    button.disabled = true;
  }
}

/**
 * Locks the submit button while the request is in flight and shows a spinner
 * in it.
 *
 * <p>The label is swapped for "Skickar …" rather than only spinning: a
 * spinner is invisible to a screen reader and to anyone who asked for less
 * motion, and aria-busy alone does not say what is happening.</p>
 */
function setBusy(form: HTMLFormElement, busy: boolean): void {
  const button = find<HTMLButtonElement>('button[type="submit"]', form);
  if (button === null) {
    return;
  }

  if (busy) {
    button.dataset["label"] ??= button.textContent ?? "";
    button.textContent = "Skickar …";
    button.setAttribute("aria-busy", "true");
  } else {
    button.textContent = button.dataset["label"] ?? button.textContent;
    button.removeAttribute("aria-busy");
  }

  button.disabled = busy;
  button.classList.toggle("btn--busy", busy);
}

/** Builds one paragraph, optionally with a single emphasised run inside it. */
function note(text: string, strong?: string, tail?: string): HTMLParagraphElement {
  const paragraph = document.createElement("p");
  paragraph.className = "form-done__note";
  paragraph.append(text);

  if (strong !== undefined) {
    const mark = document.createElement("strong");
    mark.textContent = strong;
    paragraph.append(mark, tail ?? "");
  }

  return paragraph;
}

/**
 * Replaces the form with a receipt once the message is through.
 *
 * <p>Only for forms that name a heading in the markup: the newsletter sits in
 * a band where a card in its place would look like a hole. Focus moves to the
 * receipt, which both announces it and puts a keyboard user at the text
 * instead of where a form used to be.</p>
 *
 * <p>What the receipt says comes from the markup, so each form words its own:
 * `data-done-lead` (the server's message when absent), `data-done-email-field`
 * with `data-done-email-prefix` for the line naming the address, `data-done-note`,
 * and `data-done-level` for the heading. The receipt takes the place of the
 * nearest `data-done-root`, or of the form itself.</p>
 *
 * <p>Built with DOM calls rather than innerHTML because the address is
 * whatever the visitor typed.</p>
 */
function replaceWithReceipt(form: HTMLFormElement, result: SubmitResponse): void {
  const data = form.dataset;
  const receipt = document.createElement("div");
  receipt.className = "form-done";
  receipt.tabIndex = -1;

  const title = document.createElement(data["doneLevel"] === "3" ? "h3" : "h2");
  title.textContent = data["doneHeading"] ?? "";

  const panel = document.createElement("div");
  panel.className = "form-done__panel";

  const lead = document.createElement("p");
  lead.className = "form-done__lead";
  lead.textContent = data["doneLead"] ?? result.message;
  panel.append(lead);

  // Arendenumret star for sig. Det ar det enda i kvittensen besokaren kan
  // behova skriva av, och i ett stycke lopande text hittas det inte igen.
  if (result.reference !== undefined && result.reference !== "") {
    const list = document.createElement("dl");
    list.className = "form-done__ref";

    const term = document.createElement("dt");
    term.textContent = "Ärendenummer";

    const value = document.createElement("dd");
    value.textContent = result.reference;

    list.append(term, value);
    panel.append(list);
  }

  const email = data["doneEmailField"] === undefined ? "" : field(form, data["doneEmailField"]);
  if (email !== "") {
    panel.append(note(data["doneEmailPrefix"] ?? "", email, "."));
  }
  if (data["doneNote"] !== undefined) {
    panel.append(note(data["doneNote"]));
  }

  receipt.append(title, panel);
  (form.closest("[data-done-root]") ?? form).replaceWith(receipt);
  receipt.focus();
}

/** Ifylld honungsfälla betyder robot. Vi låtsas lyckas och skickar inget. */
function isRobot(form: HTMLFormElement): boolean {
  const hp = find<HTMLInputElement>(".hp input", form);
  return hp !== null && hp.value !== "";
}

/**
 * Kontrollerar ett falt och skriver felet bredvid det.
 *
 * @returns Faltet nar det ar fel, annars null.
 */
function check(form: HTMLFormElement, field: Field): HTMLElement | null {
  const input = find<HTMLInputElement | HTMLTextAreaElement>(`#${field.id}`, form);
  // A disabled field is one the visitor's choices have taken out, such as the name when they
  // publish anonymously; it is neither checked nor sent.
  if (input === null || input.disabled) {
    return null;
  }

  const value = input.value.trim();
  const problem = ((): string | null => {
    if (value === "") {
      return field.optional === true ? null : field.emptyMessage;
    }
    if (field.kind === "email" && !EMAIL.test(value)) {
      return "Adressen saknar @ eller domän — kontrollera stavningen.";
    }
    if (field.kind === "tel" && !PHONE.test(value)) {
      return "Numret ser inte riktigt ut — siffror, mellanslag och + fungerar.";
    }
    if (field.minLength !== undefined && value.length < field.minLength) {
      return `Skriv några ord till — minst ${field.minLength} tecken.`;
    }
    if (field.noLinks === true && /:\/\/|www\./i.test(value)) {
      return "Länkar kan inte vara med. Ta bort webbadressen och försök igen.";
    }
    return null;
  })();

  if (problem === null) {
    input.removeAttribute("aria-invalid");
    setError(form, field.id, "");
    return null;
  }

  input.setAttribute("aria-invalid", "true");
  setError(form, field.id, problem);
  return input;
}

/**
 * Kontrollerar samtliga falt och returnerar det forsta felet.
 *
 * <p>Varje falt maste kontrolleras, aven nar ett tidigare redan fallit:
 * `firstError ??= check(...)` hade kortslutit och lamnat resten omarkerade,
 * sa besokaren fatt ett fel i taget i stallet for alla pa en gang.</p>
 */
function validate(form: HTMLFormElement, fields: readonly Field[]): HTMLElement | null {
  let firstError: HTMLElement | null = null;
  for (const field of fields) {
    const error = check(form, field);
    firstError ??= error;
  }
  return firstError;
}

/**
 * Svaret fran en inskicks-endpoint. Formen delas med ContactResponse och
 * NewsletterResponse i Amund.Web — den senare har inget arendenummer, sa
 * `reference` ar valfri.
 */
interface SubmitResponse {
  ok: boolean;
  reference?: string;
  message: string;
}

function field(form: HTMLFormElement, id: string): string {
  const input = find<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#${id}`, form);
  return input?.value.trim() ?? "";
}

/** Nyttolasten skiljer sig mellan formularen; endpointen avgor vilken. */
function payload(form: HTMLFormElement, endpoint: string): Record<string, unknown> {
  if (endpoint.endsWith("/api/reviews/verify")) {
    return { token: form.dataset["verification"] ?? "" };
  }

  if (endpoint.endsWith("/api/newsletter/confirm")) {
    return { token: form.dataset["confirmation"] ?? "" };
  }

  if (endpoint.endsWith("/api/reviews")) {
    return { ...reviewPayload(form), token: form.dataset["token"] ?? "" };
  }

  if (endpoint.endsWith("newsletter")) {
    return {
      email: field(form, "signup-epost"),
      website: field(form, "signup-webbplats"),
      token: form.dataset["token"] ?? "",
    };
  }

  if (endpoint.endsWith("review-request")) {
    const rating = find<HTMLInputElement>('input[name="betyg"]:checked', form);
    const accommodation = field(form, "boende");
    return {
      token: form.dataset["token"] ?? "",
      rating: rating === null ? 0 : Number(rating.value),
      text: field(form, "omdome"),
      displayName: field(form, "visningsnamn"),
      isAnonymous: find<HTMLInputElement>("#anonym", form)?.checked ?? false,
      consentToPublish: find<HTMLInputElement>("#samtycke", form)?.checked ?? false,
      category: field(form, "kategori"),
      accommodationNodeId: accommodation === "" ? null : Number(accommodation),
    };
  }

  return {
    name: field(form, "namn"),
    email: field(form, "epost"),
    phone: field(form, "telefon"),
    subject: field(form, "arende"),
    message: field(form, "meddelande"),
    website: field(form, "webbplats"),
    token: form.dataset["token"] ?? "",

    // Finns bara nar besokaren kom fran en boendesidas bokningsknapp.
    accommodation: field(form, "boende"),

    // Hidden fields are sent empty: a departure typed before the subject was changed to a wedding
    // must not reach the farm as part of a question about one day.
    arrival: tripValue(form, "ankomst"),
    departure: tripValue(form, "avresa"),
    guests: tripValue(form, "gaster"),
  };
}

/** Returns a trip field's value, or empty when its field is hidden for the chosen subject. */
function tripValue(form: HTMLFormElement, id: string): string {
  const input = find<HTMLInputElement>(`#${id}`, form);
  return input === null || input.closest("[hidden]") !== null ? "" : input.value.trim();
}

/**
 * Fits the date fields to the subject: a stay has an arrival and a departure, a wedding or a
 * conference has one day, and "Övrigt" none. The first date's label follows, and no date can be
 * picked before today — set here and not in the markup, because the page is cached and a date
 * written into it would go stale overnight.
 */
function wireTrip(form: HTMLFormElement): void {
  const row = find<HTMLElement>("[data-trip]", form);
  const subject = find<HTMLSelectElement>("#arende", form);
  const departure = find<HTMLElement>("[data-trip-departure]", form);
  const firstLabel = find<HTMLLabelElement>("[data-trip-first]", form);
  if (row === null || subject === null || departure === null || firstLabel === null) {
    return;
  }

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  for (const input of findAll<HTMLInputElement>('input[type="date"]', row)) {
    input.min = today;
  }

  let dates: TripDates | null = null;
  const labelText = firstLabel.firstChild;
  const fit = (): void => {
    const value = subject.value;
    const stay = value === "Boende";
    const event = value === "Bröllop" || value === "Konferens";
    row.hidden = !stay && !event;
    departure.hidden = !stay;
    if (labelText !== null) {
      labelText.textContent = stay ? "Ankomst " : "Datum ";
    }
    dates?.sync();
  };

  subject.addEventListener("change", fit);
  fit();

  // The calendar is a quarter of the site's script and only this form has it, so it comes in a chunk of its own and
  // every other page goes without it. Until it arrives, and for good if it never does, the browser's own date fields
  // take the dates. It is fetched after min is set, which it reads as its first day, and after fit, whose hidden
  // departure it reads as one day. Its picks are always in order, so a pick clears whatever the last submit said.
  import("./date-picker")
    .then(({ enhanceTripDates }) => {
      dates = enhanceTripDates(row, departure, () => {
        setError(form, "ankomst", "");
        setError(form, "avresa", "");
      });
    })
    .catch(() => undefined);
}

/** Checks the date fields against each other and the number of guests, the way the server will. */
function checkTrip(form: HTMLFormElement): HTMLElement | null {
  setError(form, "avresa", "");
  setError(form, "gaster", "");
  const arrival = tripValue(form, "ankomst");
  const departure = tripValue(form, "avresa");
  const guests = tripValue(form, "gaster");

  if (departure !== "" && arrival !== "" && departure <= arrival) {
    setError(form, "avresa", "Avresan behöver vara efter ankomsten.");
    // The calendar's button, where it replaced the date input: a hidden input cannot take focus.
    return find<HTMLElement>("#avresa-picker", form) ?? find<HTMLInputElement>("#avresa", form);
  }
  if (guests !== "" && !(/^[0-9]{1,3}$/.test(guests) && Number(guests) >= 1 && Number(guests) <= 300)) {
    setError(form, "gaster", "Ange antal gäster som en siffra mellan 1 och 300.");
    return find<HTMLInputElement>("#gaster", form);
  }
  return null;
}

/**
 * Skickar formularet till servern.
 *
 * Kvittensen kommer fran svaret, inte fran klienten: arendenumret finns bara
 * dar. Gar anropet inte fram sager vi det rakt ut i stallet for att latsas
 * att meddelandet kommit fram.
 */
async function send(form: HTMLFormElement, endpoint: string): Promise<void> {
  if (inFlight.has(form)) {
    return;
  }
  inFlight.add(form);
  status(form, "");
  setBusy(form, true);

  // A failure has to leave the form usable, so every path out of here either
  // hands the button back or replaces the form altogether.
  const fail = (message: string): void => {
    setBusy(form, false);
    status(form, message);
    form.classList.add("is-failed");
  };

  try {
    await prepareToken(form, true);
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const csrf = csrfHeader(form.dataset);
    if (csrf !== null) {
      headers[csrf[0]] = csrf[1];
      tokenSession(form).markAttempted();
    }
    const response = await fetch(endpoint, {
      method: "POST",
      credentials: "same-origin",
      headers,
      body: JSON.stringify(payload(form, endpoint)),
    });

    if (response.status === 429) {
      fail("För många försök just nu. Vänta en stund och prova igen.");
      return;
    }

    const result = (await response.json()) as SubmitResponse;

    if (!result.ok) {
      fail(result.message);
      return;
    }

    form.classList.remove("is-failed");
    if (form.dataset["doneHeading"] !== undefined) {
      replaceWithReceipt(form, result);
      return;
    }

    setBusy(form, false);
    status(form, result.message);
    lockButton(form);
    form.reset();
  } catch {
    fail("Något gick fel på vägen. Försök gärna igen om en stund.");
  } finally {
    inFlight.delete(form);
  }
}

/**
 * Teckenraknare for falt med tak.
 *
 * <p>Servern avvisar meddelanden over 500 tecken. Utan `maxlength` fick man
 * veta det forst nar inskicket misslyckats, och med `maxlength` men utan
 * raknare slutar webblasaren tyst ta emot text. Raknaren ar `aria-hidden` i
 * markupen — skarmlasare annonserar redan taket via `maxlength`, och en siffra
 * som andras vid varje tangenttryck hade blivit ett brus.</p>
 */
function wireCounters(form: HTMLFormElement): void {
  for (const input of findAll<HTMLTextAreaElement>("textarea[maxlength]", form)) {
    const box = find<HTMLElement>(`[data-count-for="${input.id}"]`, form);
    if (box === null) {
      continue;
    }

    const render = (): void => {
      box.textContent = `${input.value.length} / ${input.maxLength}`;
    };

    input.addEventListener("input", render);

    // Efter ett lyckat inskick nollstalls formularet. Aterstallningen sker
    // efter handelsen, sa raknaren far vanta ett varv.
    form.addEventListener("reset", () => setTimeout(render));
    render();
  }
}

function wire(
  selector: string,
  fields: readonly Field[],
  extra: ((form: HTMLFormElement) => HTMLElement | null) | null,
  receipt: string,
  robotReceipt: string,
  endpoint?: string,
): void {
  for (const form of findAll<HTMLFormElement>(selector)) {
    wireCounters(form);
    wireTrip(form);

    // Kontrollera faltet nar besokaren lamnar det. Att vanta till inskicket
    // betyder att man far reda pa alla fel samtidigt, langst ned, efter att
    // ha fyllt i hela formularet — och da har man redan glomt vad man skrev.
    // Bara falt som redan ror sig: ett tomt falt man passerar utan att skriva
    // i ska inte skalla.
    for (const field of fields) {
      const input = find<HTMLInputElement | HTMLTextAreaElement>(`#${field.id}`, form);
      input?.addEventListener("blur", () => {
        if (input.value.trim() !== "") {
          check(form, field);
        }
      });
    }

    // Rensa felet så snart användaren börjar rätta det.
    form.addEventListener("input", (event: Event) => {
      const target = event.target;
      if (target instanceof HTMLElement && target.getAttribute("aria-invalid") === "true") {
        target.removeAttribute("aria-invalid");
        setError(form, target.id, "");
      }
    });

    form.addEventListener("submit", (event: SubmitEvent) => {
      event.preventDefault();

      if (isRobot(form)) {
        if (form.dataset["doneHeading"] === undefined) {
          status(form, robotReceipt);
        } else {
          replaceWithReceipt(form, { ok: true, message: robotReceipt });
        }
        return;
      }

      const extraError = extra === null ? null : extra(form);
      const fieldError = validate(form, fields);
      const firstError = extraError ?? fieldError;

      if (firstError !== null) {
        status(form, "Det gick inte att skicka — se fälten ovan.");
        firstError.focus();
        return;
      }

      if (endpoint !== undefined) {
        void send(form, endpoint);
        return;
      }

      status(form, receipt);
      lockButton(form);
    });
    if (form.dataset["tokenLazy"] === undefined) {
      void prepareOnce(form);
    }
  }
}

/** Enable inputs only after the submission handler is attached; a failed bundle cannot submit a GET. */
function markReady(form: HTMLFormElement): void {
  const fields = find<HTMLFieldSetElement>("[data-form-fields]", form);
  if (fields !== null) {
    fields.disabled = false;
  }
  const notice = find<HTMLElement>("[data-form-unavailable]", form);
  if (notice !== null) {
    notice.hidden = true;
  }
}

export function initForms(): void {
  wire(
    "[data-contact]",
    [
      { id: "namn", emptyMessage: "Fyll i ditt namn så vi vet vem vi svarar." },
      { id: "epost", emptyMessage: "Vi behöver en e-postadress för att kunna svara.", kind: "email" },
      { id: "telefon", emptyMessage: "", kind: "tel", optional: true },
      { id: "meddelande", emptyMessage: "Skriv några rader om vad det gäller." },
    ],
    checkTrip,
    "Tack — vi återkommer inom ett dygn.",
    "Tack — vi hör av oss.",
    "/api/contact",
  );

  // The invitation form. Consent is no longer a condition for sending: without it the answer is
  // feedback for the farm, stored with ConsentToPublish false, which is what the box now says.
  wire(
    "[data-review]",
    [{ id: "omdome", emptyMessage: "Skriv några rader om vistelsen." }],
    (form) => {
      setError(form, "betyg", "");
      if (find<HTMLInputElement>('input[name="betyg"]:checked', form) === null) {
        setError(form, "betyg", "Välj ett helhetsbetyg.");
        return find<HTMLInputElement>('input[name="betyg"]', form);
      }
      return null;
    },
    "Tack! Omdömet publiceras efter granskning.",
    "Tack för ditt omdöme.",
    "/api/review-request",
  );

  wire(
    "[data-review-public]",
    [
      { id: "review-text", emptyMessage: "Skriv några rader om vistelsen.", minLength: 10, noLinks: true },
      { id: "review-name", emptyMessage: "Skriv namnet som ska stå vid omdömet, eller välj att publicera utan namn.", noLinks: true },
      { id: "review-email", emptyMessage: "Vi behöver din e-post för att skicka länken som bekräftar omdömet.", kind: "email" },
    ],
    checkReviewChoices,
    "Tack för ditt omdöme!",
    "Tack för ditt omdöme!",
    "/api/reviews",
  );

  // The confirmation pages' one button each. Neither needs a form token: the link the page came from is the secret.
  wire("[data-review-verify]", [], null, "", "", "/api/reviews/verify");
  wire("[data-newsletter-confirm]", [], null, "", "", "/api/newsletter/confirm");

  initReviewForms(prepareOnce);
  initNewsletter();
}

/**
 * Nyhetsbrevet i sidfoten.
 *
 * <p>Egen hantering i stallet for `wire`: rutan har ett enda falt och ingen
 * plats for felrutor bredvid faltet, sa felet skrivs i statusraden — som redan
 * ar ett `aria-live`-omrade.</p>
 */
function initNewsletter(): void {
  for (const form of findAll<HTMLFormElement>("[data-signup]")) {
    form.addEventListener("submit", (event: SubmitEvent) => {
      event.preventDefault();
      const input = find<HTMLInputElement>('input[type="email"]', form);
      if (input === null) {
        return;
      }

      // Ifylld honungsfalla betyder robot. Vi latsas lyckas och skickar inget.
      if (isRobot(form)) {
        status(form, "Tack! Kolla din inkorg och bekräfta prenumerationen. Länken gäller i 72 timmar.");
        lockButton(form);
        return;
      }

      const value = input.value.trim();
      if (value === "") {
        status(form, "Fyll i din e-postadress.");
        input.setAttribute("aria-invalid", "true");
        input.focus();
        return;
      }
      if (!EMAIL.test(value)) {
        status(form, "Adressen saknar @ eller domän — kontrollera stavningen.");
        input.setAttribute("aria-invalid", "true");
        input.focus();
        return;
      }

      input.removeAttribute("aria-invalid");
      void send(form, "/api/newsletter");
    });
    void prepareOnce(form);
  }
}
